class CurrentUserService {

    constructor(
        $http,
        $state,
        BrowserCheck,
        UserHelper,
        appConfig
    ) {
        'ngInject';

        this.$http = $http;
        this.$state = $state;
        this.BrowserCheck = BrowserCheck;
        this.UserHelper = UserHelper;
        this.appConfig = appConfig;

        this.user = null;

        this.roleIdToPermissionCodes = {};
        this.permissionCodeToPermissionId = {};
    }

    /***
     * Log out the current user.
     */
    logoutUser() {
        return this.$http.post(this.appConfig.apiUrl + '/auth/logout/');
    }

    /***
     * Update this Service with information about the user currently logged into the backend.
     *
     * Used as a Resolve before any state change to make sure that any changes to a user that
     * occur while the user is on the site (e.g. an Admin updates their status) is reflected
     * on the next state change.
     *
     * Can also take in an optional timerCallbackFunction that will be called right before a user
     * is automatically logged out from 30 minutes of inactivity.
     *
     * @param params                            optional URL params
     * @param timerCallbackFunction             an optional function that will be called before auto-logging the user
     *                                          out from the idle timer.
     * @param timerCallbackFunctionContext      the context for the timerCallbackFunction (usually the Service that the
     *                                          function belongs to). This ensures that any references to "this" within
     *                                          the timerCallbackFunction will reference its own Service and not
     *                                          CurrentUserService.
     */
    updateCurrentUser(params, timerCallbackFunction, timerCallbackFunctionContext) {
        // Before updating the user, verify that they're using Chrome. If not, don't update the current user,
        // effectively blocking the rest of the site from loading.
        // if (!this.BrowserCheck.isChrome()) {
        //     this.autoLogout();
        //     alert("The browser that you are using is not supported by this tool.  Please relaunch the tool using "
        //         + "Google Chrome.");
        //     return;
        // }

        const config = params ? {params: params} : {};
        return this.$http.get(this.appConfig.apiUrl + '/users/current/', config)
            .then((successResponse) => {
                this.user = successResponse.data.user;
                this.canViewClientPage = successResponse.data.can_view_client_page;
                this.logoutTimerFunction = timerCallbackFunction;
                this.timerCallbackFunctionContext = timerCallbackFunctionContext || this;
            }, (errorResponse)=>  {
                if (errorResponse.status == 403) {
                    this.user = null;
                }
            }
        );
    }

    /***
     * If a function has been set to be called when the logout timer begins idling, call it.
     */
    logoutIdleCallback() {
        if (this.logoutTimerFunction) {
            this.logoutTimerFunction.call(this.timerCallbackFunctionContext);
        }
    }

    /***
     *  A function that is called after the idle timer expires. Automatically logs out the user. In addition, this
     *  may call a custom logoutTimerFunction, depending on what page you are on. (for example, on the Enter Data
     *  page, we call a logoutTimerFunction that saves any unsaved data before logging the user out).
     *
     *  We make sure to apply the context for the function so the correct "this" is used when calling the function.
     */
    autoLogout() {
        this.logoutUser().then(() => {
            this.$state.go('app.home', {}, {reload: true});
        })
    }

    canViewPage() {
        return this.canViewClientPage || false;
    }

    isUserAdmin() {
        return this.UserHelper.hasPermission(this.user, 'USER_ADMIN')
    }

    /***
     * Check if the current user is a Client Admin.
     *
     * @returns {boolean}
     */
    isClientAdmin() {
        return this.UserHelper.hasPermission(this.user, 'CLIENT_ADMIN');
    }

    /***
     * Check if the current user is a Facility Admin.
     *
     * @returns {boolean}
     */
    isFacilityAdmin() {
        return this.UserHelper.hasPermission(this.user, 'FACILITY_ADMIN');
    }





    canViewAllClients() {
        return this.UserHelper.hasPermission(this.user, 'VIEW_ALL_CLIENTS', true);
    }

    canViewAllPhysicalTherapyClients() {
        return this.UserHelper.hasPermission(this.user, 'VIEW_PT_CLIENTS', true);
    }

    canAddClientLocationFacility() {
        return this.UserHelper.hasPermission(this.user, 'ADD_CLIENT_LOCATION_FACILITY');
    }

    /***
     * Check if the current user is a User.
     *
     * @returns {boolean}
     */
    isUser() {
        return this.user && this.user.status === 'User';
    }

    /***
     * Check if the current user is in the Holding state.
     *
     * @returns {boolean}
     */
    isHolding() {
        return this.user && this.user.status === 'Holding';
    }

    /***
     * Check if the current user is Deactivated.
     *
     * @returns {boolean}
     */
    isDeactivated() {
        return this.user && this.user.status === 'Deactivated';
    }

    /***
     * Check if there is currently a user logged in.
     *
     * @returns {boolean}
     */
    isLoggedIn() {
        return this.user !== null;
    }

}

export default CurrentUserService;



// WEBPACK FOOTER //
// ./app/common/current-user/current-user-service.js